import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import { ChevronLeft, IkonateContext } from "react-ikonate";
import { sortBy, groupBy, clone } from "lodash-es";
import { TagMonsterChild } from "../TagMonsterChild";
import styles from "./index.module.css";

const MAX_PREVIEW_ROWS = 4;
const MAX_CATEGORIES_PER_ROW = 4;

function arrangePreviewCategories(categories) {
  const publishedCategories = categories.filter(
    (category) => category.isPublished
  );
  const categoriesByParent = groupBy(
    publishedCategories,
    (category) => category.parentCategoryID ?? "root"
  );
  const rowByCategory = new Map();
  const hiddenCategories = new Set();

  Object.values(categoriesByParent).forEach((siblings) => {
    const rows = Array.from({ length: MAX_PREVIEW_ROWS }, () => []);
    const overflow = [];

    sortBy(siblings, "rowNumber", "stepNumber").forEach((category) => {
      const desiredRow = Math.max(
        0,
        Math.min(MAX_PREVIEW_ROWS - 1, Number(category.rowNumber || 1) - 1)
      );

      if (rows[desiredRow].length < MAX_CATEGORIES_PER_ROW) {
        rows[desiredRow].push(category);
      } else {
        overflow.push({ category, desiredRow });
      }
    });

    overflow.forEach(({ category, desiredRow }) => {
      let availableRow = rows.findIndex(
        (row, index) =>
          index > desiredRow && row.length < MAX_CATEGORIES_PER_ROW
      );

      if (availableRow === -1) {
        availableRow = rows.findIndex(
          (row) => row.length < MAX_CATEGORIES_PER_ROW
        );
      }

      if (availableRow === -1) {
        hiddenCategories.add(category);
      } else {
        rows[availableRow].push(category);
      }
    });

    rows.forEach((row, index) => {
      row.forEach((category) => rowByCategory.set(category, index + 1));
    });
  });

  return {
    categories: publishedCategories
      .filter((category) => !hiddenCategories.has(category))
      .map((category) => ({
        ...category,
        rowNumber: rowByCategory.get(category),
      })),
    overflowCount: hiddenCategories.size,
  };
}

export default function TagMonster({ categories }) {
  const [catOpened, setCatOpened] = useState(0);
  const [subCatOpened, setSubCatOpened] = useState(0);
  const [subSubCatOpened, setSubSubCatOpened] = useState(0);

  const [error, setError] = useState(null);

  const catClicked = (e) => {
    const num = parseInt(e);

    if (catOpened === num) {
      // setCatOpened(0);
    } else {
      setCatOpened(num);
    }
  };

  const subCatClicked = (e) => {
    const num = parseInt(e);

    if (subCatOpened === num) {
      setSubCatOpened(0);
    } else {
      setSubCatOpened(num);
      setSubSubCatOpened(0);
    }
  };

  const subSubCatClicked = (e) => {
    const num = parseInt(e);

    if (subSubCatOpened === num) {
      setSubSubCatOpened(0);
    } else {
      setSubSubCatOpened(num);
    }
  };

  const backBtnClicked = () => {
    setCatOpened(0);
    setSubCatOpened(0);
    setSubSubCatOpened(0);
  };

  let rows = [];
  let originalData = categories;

  const hierarchicalData = useMemo(() => {
    if (originalData) {
      let finalData = [];
      let originalDataCopy = clone(originalData);

      // remove all categories that cannot be sorted
      originalDataCopy = originalDataCopy.filter((x) => {
        let foundParent = false;
        for (const y of originalDataCopy) {
          if (x.parentCategoryID == null || y.id == x.parentCategoryID) {
            foundParent = true;
            break;
          }
        }

        return foundParent;
      });

      // failsafe in case the data is really messed up
      let passes = 0;

      // sort data in several passes, break out of loop if passes hits 100 because nothing should ever be nested that deep
      while (originalDataCopy.length > 0 && passes < 100) {
        passes++;

        let dataToAdd = [];

        if (finalData.length == 0) {
          // get top level categories
          dataToAdd = originalDataCopy.filter((x) => {
            return x.parentCategoryID == null;
          });
        } else {
          // get child categories
          dataToAdd = originalDataCopy.filter((x) => {
            for (const y of finalData) {
              if (y.id == x.parentCategoryID) {
                return x;
              }
            }
          });
        }

        // remove data from original copy to prevent infinite while loop
        originalDataCopy = originalDataCopy.filter((x) => {
          let foundData = false;
          for (const y of dataToAdd) {
            if (x.id == y.id) {
              foundData = true;
              break;
            }
          }

          return !foundData;
        });

        // sort before adding to finalData
        dataToAdd = sortBy(
          dataToAdd,
          "rowNumber",
          "stepNumber",
          "parentCategoryId"
        );

        finalData = [...finalData, ...dataToAdd];
      }

      return finalData;
    }
  }, [originalData]);

  const { categories: data, overflowCount } = useMemo(
    () => arrangePreviewCategories(hierarchicalData || []),
    [hierarchicalData]
  );
  const getRowCount = (rowNumber, parentCategoryID) =>
    data.filter(
      (category) =>
        category.rowNumber === rowNumber &&
        category.parentCategoryID === parentCategoryID
    ).length;
  const hasSubcategories = (categoryId) =>
    data.some((category) => category.parentCategoryID === categoryId);

  return (
    <>
      {data !== null && data.length > 0 ? (
        <>
          <div className={styles.container}>
            {data.map((d, index) => {
              if (catOpened === 0) {
                if (!rows.includes(d.rowNumber)) {
                  rows.push(d.rowNumber);
                  return d.parentCategoryID === null ? (
                    <div
                      className={styles.row}
                      data-count={getRowCount(d.rowNumber, null)}
                    >
                      {data.map((data, index) => {
                        return data.rowNumber === d.rowNumber &&
                          data.parentCategoryID === null &&
                          data.isPublished ? (
                          <TagMonsterChild
                            onClick={
                              hasSubcategories(data.id)
                                ? (e) => catClicked(e.target.value)
                                : undefined
                            }
                            style={
                              data.colorFill == true
                                ? {
                                    backgroundColor: data.backgroundColor,
                                    color: data.textColor,
                                  }
                                : {
                                    color: data.textColor,
                                  }
                            }
                            className={styles.col}
                            value={data.id}
                          >
                            {data.title}
                          </TagMonsterChild>
                        ) : (
                          ""
                        );
                      })}
                    </div>
                  ) : (
                    ""
                  );
                }
              } else {
                return d.id == catOpened ? (
                  <div className={styles.row2}>
                    <button onClick={backBtnClicked} className={styles.backBtn}>
                      <ChevronLeft className={styles.backBtnIcon} />
                    </button>
                    <TagMonsterChild
                      onClick={
                        hasSubcategories(d.id)
                          ? (e) => catClicked(e.target.value)
                          : undefined
                      }
                      style={{
                        backgroundColor: d.backgroundColor,
                        color: d.textColor,
                      }}
                      className={styles.col}
                      value={d.id}
                    >
                      {d.title}
                    </TagMonsterChild>
                  </div>
                ) : !rows.includes(d.rowNumber) &&
                  d.parentCategoryID === catOpened ? (
                  (rows.push(d.rowNumber),
                  d.parentCategoryID === catOpened ? (
                    <div
                      className={styles.row}
                      data-count={getRowCount(d.rowNumber, catOpened)}
                    >
                      {data.map((data, index) => {
                        return data.rowNumber === d.rowNumber &&
                          data.parentCategoryID === catOpened &&
                          data.isPublished ? (
                          <>
                            <TagMonsterChild
                              onClick={
                                hasSubcategories(data.id)
                                  ? (e) => subCatClicked(e.target.value)
                                  : undefined
                              }
                              style={
                                data.colorFill == true ||
                                data.id === subCatOpened
                                  ? {
                                      backgroundColor: data.backgroundColor,
                                      color: data.textColor,
                                    }
                                  : {
                                      color: data.textColor,
                                    }
                              }
                              className={styles.col}
                              value={data.id}
                            >
                              {data.title}
                            </TagMonsterChild>
                          </>
                        ) : (
                          ""
                        );
                      })}
                    </div>
                  ) : (
                    ""
                  ))
                ) : (
                  ""
                );
              }
            })}

            <div style={{ display: "none" }}>{(rows = [])}</div>

            {data.map((d, index) => {
              if (subCatOpened !== 0 && !rows.includes(d.rowNumber)) {
                rows.push(d.rowNumber);
                return (
                  <div
                    className={styles.row}
                    data-count={getRowCount(d.rowNumber, subCatOpened)}
                  >
                    {data.map((data, index) => {
                      return data.rowNumber === d.rowNumber &&
                        data.parentCategoryID === subCatOpened &&
                        data.isPublished ? (
                        <>
                          <TagMonsterChild
                            onClick={
                              hasSubcategories(data.id)
                                ? (e) => subSubCatClicked(e.target.value)
                                : undefined
                            }
                            style={
                              data.colorFill == true ||
                              data.id === subSubCatOpened
                                ? {
                                    backgroundColor: data.backgroundColor,
                                    color: data.textColor,
                                  }
                                : {
                                    color: data.textColor,
                                  }
                            }
                            className={styles.col}
                            value={data.id}
                          >
                            {data.title}
                          </TagMonsterChild>
                        </>
                      ) : (
                        ""
                      );
                    })}
                  </div>
                );
              }
            })}

            <div style={{ display: "none" }}>{(rows = [])}</div>

            {data.map((d, index) => {
              if (subSubCatOpened !== 0 && !rows.includes(d.rowNumber)) {
                rows.push(d.rowNumber);
                return (
                  <div
                    className={styles.row}
                    data-count={getRowCount(d.rowNumber, subSubCatOpened)}
                  >
                    {data.map((data, index) => {
                      return data.rowNumber === d.rowNumber &&
                        data.parentCategoryID === subSubCatOpened &&
                        data.isPublished ? (
                        <>
                          <TagMonsterChild
                            style={
                              data.colorFill == true
                                ? {
                                    backgroundColor: data.backgroundColor,
                                    color: data.textColor,
                                  }
                                : {
                                    color: data.textColor,
                                  }
                            }
                            className={styles.col}
                            value={data.id}
                          >
                            {data.title}
                          </TagMonsterChild>
                        </>
                      ) : (
                        ""
                      );
                    })}
                  </div>
                );
              }
            })}
          </div>
          {overflowCount > 0 && (
            <p className={styles.previewLimit} role="status">
              {overflowCount} published categories exceed the 4 row by 4 tag
              preview limit and are not shown.
            </p>
          )}
        </>
      ) : data && data.length < 1 ? (
        <>No categories to display. Try adding some.</>
      ) : (
        <>error{error}</>
      )}
    </>
  );
}

TagMonster.propTypes = {
  /**
   * controls what item is selected
   */
  catOpened: PropTypes.number,
  subCatOpened: PropTypes.number,
  subSubCatOpened: PropTypes.number,
};
